import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SiteNav, MinimalFooter, useReveal, useTopProgress, useStartAnfrage } from './shared';
import Seo from './Seo';

const CDN_BASE_URL = (process.env.REACT_APP_CDN_BASE_URL || 'https://hamba.fra1.cdn.digitaloceanspaces.com').replace(/\/$/, '');

const toCdnUrl = (path) => {
  if (!path || /^https?:\/\//i.test(path)) return path;
  const clean = path.replace(/^\.\//, '').replace(/^(\.\.\/)+/, '').replace(/^\/+/, '');
  return `${CDN_BASE_URL}/${clean}`;
};

const monthMap = {
  Januar: '01', Februar: '02', März: '03', April: '04', Mai: '05', Juni: '06',
  Juli: '07', August: '08', September: '09', Oktober: '10', November: '11', Dezember: '12',
};

const toIsoDate = (germanDate) => {
  const [day, monthName, year] = germanDate.split(' ');
  return `${year}-${monthMap[monthName] || '01'}-${day.replace('.', '').padStart(2, '0')}`;
};

const formatShort = (germanDate) => {
  const [day, monthName, year] = germanDate.split(' ');
  return `${day.replace('.', '').padStart(2, '0')}.${monthMap[monthName] || '01'}.${year}`;
};

const CATEGORIES = ['Alle', 'News', 'Vermietung', 'Verwaltung'];

export default function BlogOverview({ articles }) {
  useTopProgress();
  const startAnfrage = useStartAnfrage();
  const [visible, setVisible] = useState(9);
  const [category, setCategory] = useState('Alle');
  useReveal([category, visible]);

  const sorted = [...articles].sort(
    (a, b) => new Date(toIsoDate(b.date)) - new Date(toIsoDate(a.date))
  );
  const filtered = category === 'Alle'
    ? sorted
    : sorted.filter((a) => a.category.toLowerCase() === category.toLowerCase());

  return (
    <div className="page-enter" data-screen-label="Journal · Übersicht">
      <div id="homev2-progress" className="progress-bar" />
      <Seo
        path="/blog"
        title="Journal — Wissen zu Immobilien & Verwaltung — hamba"
        description="Fachbeiträge zu Immobilienbewertung, Vermietung, Betriebskosten und Verwaltung — aus der Praxis unseres Immobilien Family Office im Rhein-Main-Gebiet."
      />
      <SiteNav />

      <section style={{ paddingTop: 80, paddingBottom: 60 }}>
        <div className="shell">
          <div className="reveal" style={{ maxWidth: 720 }}>
            <div className="section-label" style={{ marginBottom: 24 }}>
              <span>Journal</span>
            </div>
            <h1
              className="h-display"
              style={{
                fontSize: 'clamp(40px, 5vw, 64px)',
                letterSpacing: '-0.005em',
                marginBottom: 24,
                lineHeight: 1.05,
              }}
            >
              Einblicke in die Immobilienverwaltung.
            </h1>
            <p
              className="lede"
              style={{
                fontSize: 19,
                fontStyle: 'italic',
                fontFamily: 'var(--serif)',
                color: 'var(--charcoal-muted)',
                maxWidth: '60ch',
                lineHeight: 1.55,
              }}
            >
              Beobachtungen aus der Praxis. Analysen, Konzepte und Hintergrundwissen
              für Eigentümer.
            </p>
          </div>
        </div>
      </section>

      <section style={{ paddingBottom: 60 }}>
        <div className="shell">
          <div
            className="reveal"
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              borderBottom: '1px solid var(--cream-line)',
              paddingBottom: 28,
              marginBottom: 56,
            }}
          >
            {CATEGORIES.map((cat) => {
              const active = category === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => { setCategory(cat); setVisible(9); }}
                  style={{
                    fontFamily: 'var(--grotesk)',
                    fontSize: 12,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    padding: '10px 18px',
                    background: active ? 'var(--anthracite)' : 'transparent',
                    color: active ? 'var(--cream)' : 'var(--charcoal-muted)',
                    border: `1px solid ${active ? 'var(--anthracite)' : 'var(--cream-line)'}`,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'background 180ms ease, color 180ms ease, border-color 180ms ease',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = 'var(--anthracite)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = 'var(--cream-line)'; }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 48,
              rowGap: 64,
            }}
          >
            {filtered.slice(0, visible).map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="reveal"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  color: 'var(--anthracite)',
                  group: 'card',
                }}
              >
                <div
                  style={{
                    aspectRatio: '4 / 3',
                    overflow: 'hidden',
                    background: 'var(--cream-deep)',
                    border: '1px solid var(--cream-line)',
                    marginBottom: 20,
                  }}
                >
                  <img
                    src={toCdnUrl(article.thumbnail)}
                    alt={article.title}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 400ms ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    alignItems: 'center',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'var(--charcoal-muted)',
                    marginBottom: 10,
                  }}
                >
                  <span style={{ color: 'var(--bordeaux)' }}>{article.category}</span>
                  <span style={{ color: 'var(--cream-line)' }}>·</span>
                  <span>{formatShort(article.date)}</span>
                </div>

                <h2
                  style={{
                    fontFamily: 'var(--serif)',
                    fontSize: 22,
                    lineHeight: 1.25,
                    letterSpacing: '-0.005em',
                    color: 'var(--anthracite)',
                    margin: 0,
                  }}
                >
                  {article.title}
                </h2>
              </Link>
            ))}
          </div>

          {visible < filtered.length && (
            <div style={{ textAlign: 'center', marginTop: 72 }}>
              <button
                type="button"
                onClick={() => setVisible((v) => v + 9)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '14px 32px',
                  background: 'transparent',
                  color: 'var(--anthracite)',
                  border: '1px solid var(--anthracite)',
                  fontFamily: 'var(--grotesk)',
                  fontSize: 13,
                  letterSpacing: '0.08em',
                  cursor: 'pointer',
                  borderRadius: 1,
                  transition: 'background 200ms ease, color 200ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--anthracite)'; e.currentTarget.style.color = 'var(--cream)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--anthracite)'; }}
              >
                Weitere Artikel laden
              </button>
            </div>
          )}
        </div>
      </section>

      <MinimalFooter />
    </div>
  );
}
